import dayjs from "dayjs"
import type {
  DunningCaseContractDetail,
  DunningCaseFacts,
  DunningCaseFactsContract,
  DunningCaseItem,
  DunningChatMessage,
  DunningContactLogEntry,
  DunningPaymentPromise,
  DunningPaymentScheduleEntry,
  DunningUserMemo,
} from "../types"
import type { PromiseSource, Scenario, ScenarioContract, ScenarioInstallment } from "./types"

/**
 * 検証ケースの scenario を、実際の督促ケース詳細の部品が受け取る形（DunningCaseItem / facts）に変換する。
 * AI に渡る入力（smooth_api の DunningEvaluationScenario#context）と同じ組み立てにそろえ、
 * 画面に出る値と AI が受け取る値を一致させる:
 * - お客様の名前は「検証 太郎」
 * - 契約（contracts）があれば、契約ごとの分割予定をそのまま出す。遅延金額は延滞が確定した回の未払い額の合計
 *   （振替請求ありなら結果日の13時、なしなら振替日の翌日、振替日程が無ければ約定日の翌日に確定）
 * - 契約が無い古いケースは、架空の契約1件（第2回が遅延分、第3回が次回・30,000円）
 * - 有効な約束・約束の履歴・交渉記録・メモは、それぞれ支払約束・交渉記録・メモの部品で出す
 * - 事実メモ（古い形式）はオペレーターメモとして渡る
 */

/** AI 入力と同じお客様の名前（smooth_api の DunningEvaluationScenario#context） */
export const EVALUATION_USER_NAME = "検証 太郎"
/** 古いケースの架空の契約 */
const LEGACY_CONTRACT_ID = 1
const LEGACY_NUMBER_OF_PAYMENT = 12
const LEGACY_NEXT_INSTALLMENT_AMOUNT = 30_000
/** 事実メモ・メモ・交渉記録の記入者として出す名前 */
export const FACTS_AUTHOR = "検証ケース"
/** 有効な約束の ID（約束の履歴の連番と重ならないように大きくする） */
const ACTIVE_PROMISE_ID = 1000

export const PROMISE_SOURCE_LABELS: Record<PromiseSource, string> = {
  dunning_payment_promise: "督促画面",
  bank_transfer_invoice: "振込請求",
}

/**
 * AI に渡る接触回数（smooth_api の DunningEvaluationScenario#contact_count_total と同じ規則）。
 * 指定が無ければ、当社の送信と、当社から連絡した交渉記録（連絡回数に数えないものを除く）を数える
 */
export function scenarioContactCount(scenario: Scenario): number {
  if (scenario.contactCountTotal != null) return scenario.contactCountTotal
  const sent = scenario.conversation.filter((message) => message.direction === "outbound").length
  const logs = (scenario.contactLogs ?? []).filter(
    (log) => log.direction === "outbound" && log.countsAsContact !== false
  ).length
  return sent + logs
}

/** ISO8601 の日時を日本時間の日付（YYYY-MM-DD）にする */
export const jstDate = (iso: string) => {
  const time = Date.parse(iso)
  // 日時の欄を一部消した途中の値（空・不正）でも画面を落とさない
  return Number.isNaN(time) ? "" : new Date(time + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/** 分割の状態。AI 入力の区分（過去の入金・期日超過・振替結果待ち・今後の請求予定）と同じ */
export type InstallmentState = "paid" | "overdue" | "pending" | "upcoming"

export type DerivedInstallment = ScenarioInstallment & {
  paymentPromiseId: number
  state: InstallmentState
}

export type DerivedContract = {
  contractId: number
  contract: ScenarioContract
  installments: DerivedInstallment[]
  /** 延滞が確定した回の未払い額の合計 */
  overduePrincipal: number
}

const outstanding = (row: ScenarioInstallment) =>
  Math.max(row.paymentTotalAmount - row.paidAmount, 0)

/** 延滞が確定しているか（smooth_api の DunningEvaluationContractBreakdown と同じ規則） */
const confirmed = (row: ScenarioInstallment, asOf: string, today: string) => {
  if (!row.transferOn && !row.resultOn) return true
  if (row.transferRequested)
    return !!row.resultOn && Date.parse(`${row.resultOn}T13:00:00+09:00`) <= Date.parse(asOf)
  return !!row.transferOn && row.transferOn < today
}

/** 契約の分割予定を AI 入力と同じ区分に分ける。ID は架空の連番（契約は1から、分割は全契約を通して1から） */
export function deriveContracts(scenario: Scenario): DerivedContract[] {
  const today = jstDate(scenario.asOf)
  let nextId = 0
  return (scenario.contracts ?? []).map((contract, index) => {
    const installments = contract.installments
      .map((row, position) => ({ row, position }))
      .sort((a, b) =>
        a.row.scheduledOn === b.row.scheduledOn
          ? a.position - b.position
          : a.row.scheduledOn < b.row.scheduledOn
            ? -1
            : 1
      )
      .map(({ row }): DerivedInstallment => {
        nextId += 1
        const state: InstallmentState =
          row.paidAmount >= row.paymentTotalAmount
            ? "paid"
            : row.scheduledOn >= today
              ? "upcoming"
              : confirmed(row, scenario.asOf, today)
                ? "overdue"
                : "pending"
        return { ...row, paymentPromiseId: nextId, state }
      })
    return {
      contractId: index + 1,
      contract,
      installments,
      overduePrincipal: installments
        .filter((row) => row.state === "overdue")
        .reduce((sum, row) => sum + outstanding(row), 0),
    }
  })
}

/** 契約があれば分割予定から導いた遅延金額（保存時にサーバーでもこの値にそろえる）。無ければ入力値 */
export function scenarioPrincipalDue(scenario: Scenario): number {
  if (!scenario.contracts?.length) return scenario.principalDue
  return deriveContracts(scenario).reduce((sum, contract) => sum + contract.overduePrincipal, 0)
}

/** 契約があれば今後の請求予定のうち最も早い約定日。無ければ入力値 */
export function scenarioNextDueOn(scenario: Scenario): string {
  const upcoming = deriveContracts(scenario)
    .flatMap((contract) => contract.installments)
    .filter((row) => row.state === "upcoming")
    .map((row) => row.scheduledOn)
    .sort()
  return upcoming[0] ?? scenario.nextDueOn
}

/** 遅延分の約定日（基準日から遅延日数だけ前） */
const overdueScheduledOn = (scenario: Scenario) =>
  dayjs(scenario.asOf).subtract(scenario.overdueDays, "day").format("YYYY-MM-DD")

/** 未応答（会話の末尾がお客様）なら要返信 */
const awaitingReplySince = (scenario: Scenario) => {
  const sorted = [...scenario.conversation].sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
  const last = sorted[sorted.length - 1]
  return last?.direction === "inbound" ? last.at : null
}

const scheduleEntry = (row: DerivedInstallment): DunningPaymentScheduleEntry => ({
  paymentPromiseId: row.paymentPromiseId,
  numberOfPayment: row.numberOfPayment,
  scheduledOn: row.scheduledOn,
  paymentTotalAmount: row.paymentTotalAmount,
  paidAmount: row.paidAmount,
  paymentStatus:
    row.paidAmount >= row.paymentTotalAmount ? "paid" : row.paidAmount > 0 ? "part_paid" : "unpaid",
  lastPaidAt: row.lastPaidAt ?? null,
  transferOn: row.transferOn ?? null,
  resultOn: row.resultOn ?? null,
  transferRequested: !!row.transferRequested,
})

/** 契約の facts・詳細（契約があるケース） */
function contractFacts(scenario: Scenario): {
  contracts: DunningCaseFactsContract[]
  details: DunningCaseContractDetail[]
} {
  const derived = deriveContracts(scenario)
  const single = derived.length === 1
  return {
    contracts: derived.map((contract) => ({
      contractId: contract.contractId,
      active: true,
      overdueDays: scenario.overdueDays,
      principalDue: contract.overduePrincipal,
      // 遅延損害金はケース全体の値しか持たないので、契約が1件のときだけ契約に付ける
      lateChargeDue: single ? scenario.lateChargeDue : undefined,
    })),
    details: derived.map((contract) => ({
      contractId: contract.contractId,
      active: true,
      numberOfPayment: contract.contract.numberOfPayment,
      loan: null,
      interestAmount: null,
      interestRate: null,
      totalPaymentAmount: contract.contract.totalPaymentAmount,
      propertyName: contract.contract.propertyName,
      movingApplicationId: null,
      paymentSchedule: contract.installments.map(scheduleEntry),
    })),
  }
}

/** 契約の facts・詳細（契約を持たない古いケース） */
function legacyContractFacts(scenario: Scenario): {
  contracts: DunningCaseFactsContract[]
  details: DunningCaseContractDetail[]
} {
  return {
    contracts: [
      {
        contractId: LEGACY_CONTRACT_ID,
        active: true,
        overdueDays: scenario.overdueDays,
        principalDue: scenario.principalDue,
        lateChargeDue: scenario.lateChargeDue,
      },
    ],
    details: [
      {
        contractId: LEGACY_CONTRACT_ID,
        active: true,
        numberOfPayment: LEGACY_NUMBER_OF_PAYMENT,
        loan: null,
        interestAmount: null,
        interestRate: null,
        totalPaymentAmount: null,
        propertyName: "架空の検証用契約",
        movingApplicationId: null,
        paymentSchedule: [
          ...(scenario.principalDue > 0
            ? [
                {
                  paymentPromiseId: 1,
                  numberOfPayment: 2,
                  scheduledOn: overdueScheduledOn(scenario),
                  paymentTotalAmount: scenario.principalDue,
                  paidAmount: 0,
                  paymentStatus: "unpaid" as const,
                  lastPaidAt: null,
                  transferOn: null,
                  resultOn: null,
                  transferRequested: false,
                },
              ]
            : []),
          {
            paymentPromiseId: 2,
            numberOfPayment: 3,
            scheduledOn: scenario.nextDueOn,
            paymentTotalAmount: LEGACY_NEXT_INSTALLMENT_AMOUNT,
            paidAmount: 0,
            paymentStatus: "unpaid",
            lastPaidAt: null,
            transferOn: null,
            resultOn: null,
            transferRequested: false,
          },
        ],
      },
    ],
  }
}

/**
 * 支払約束パネルに渡す約束。有効な約束と約束の履歴（支払済・不履行・期日前）。
 * 約束の履歴は本番の AI 入力と同じく結果だけを持つので、状態の代わりに結果（historyResult）を出す
 */
export function scenarioPromises(scenario: Scenario): DunningPaymentPromise[] {
  const active = scenario.activePromise
  const history = (scenario.promiseHistory ?? []).map((promise, index): DunningPaymentPromise => ({
    id: index + 1,
    contractId: LEGACY_CONTRACT_ID,
    scheduledAt: promise.scheduledOn,
    amount: promise.amount,
    note: promise.source ? `登録元 ${PROMISE_SOURCE_LABELS[promise.source]}` : null,
    status: "superseded",
    broken: false,
    createdBySmootherId: null,
    createdAt: promise.promisedAt,
    historyResult: promise.fulfilled
      ? "fulfilled"
      : Date.parse(promise.scheduledOn) < Date.parse(scenario.asOf)
        ? "broken"
        : "pending",
  }))
  if (!active) return history
  return [
    {
      id: ACTIVE_PROMISE_ID,
      contractId: LEGACY_CONTRACT_ID,
      scheduledAt: active.scheduledOn,
      amount: active.amount,
      note: active.source ? `登録元 ${PROMISE_SOURCE_LABELS[active.source]}` : null,
      status: "active",
      broken: false,
      outcome: "pending",
      current: true,
      createdBySmootherId: null,
      // 本番の AI 入力は有効な約束の登録日時を持たない
      createdAt: "",
    },
    ...history,
  ]
}

/** 交渉記録パネルに渡す記録（新しい順） */
export function scenarioContactLogs(scenario: Scenario): DunningContactLogEntry[] {
  return (scenario.contactLogs ?? [])
    .map((log, index): DunningContactLogEntry => ({
      id: index + 1,
      channel: log.channel,
      direction: log.direction,
      contactedAt: log.contactedAt,
      outcome: log.outcome ?? null,
      phoneLine: log.phoneLine ?? null,
      counterpart: log.counterpart ?? null,
      contactResult: log.contactResult ?? null,
      contactDetail: log.contactDetail ?? null,
      note: log.note ?? null,
      countsAsContact: log.countsAsContact !== false,
      smootherId: null,
      recordedByName: FACTS_AUTHOR,
      contractId: null,
    }))
    .sort((a, b) => Date.parse(b.contactedAt) - Date.parse(a.contactedAt) || b.id - a.id)
}

/** メモ欄に出すメモ（古い順）。事実メモ（古い形式）は基準日時のメモとして最後に出す */
export function scenarioMemos(
  scenario: Scenario
): Pick<DunningUserMemo, "title" | "body" | "authorName" | "updatedAt">[] {
  const memos = [...(scenario.operatorMemos ?? [])]
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
    .map((memo) => ({
      title: memo.title?.trim() || null,
      body: memo.body,
      authorName: FACTS_AUTHOR,
      updatedAt: memo.createdAt,
    }))
  return scenario.facts.trim()
    ? [
        ...memos,
        {
          title: "事実メモ",
          body: scenario.facts,
          authorName: FACTS_AUTHOR,
          updatedAt: scenario.asOf,
        },
      ]
    : memos
}

export function scenarioFacts(scenario: Scenario): DunningCaseFacts {
  const structured = !!scenario.contracts?.length
  const { contracts, details } = structured
    ? contractFacts(scenario)
    : legacyContractFacts(scenario)
  const principalDue = scenarioPrincipalDue(scenario)
  const totalDue = principalDue + scenario.lateChargeDue
  const lastCustomerMessageAt = awaitingReplySince(scenario)
  const contactLogs = scenarioContactLogs(scenario)
  const lineBlocked = !!scenario.lineBlocked
  return {
    asOf: scenario.asOf,
    refreshStatus: "ok",
    contractCount: contracts.length,
    contracts,
    amounts: {
      overdueDays: scenario.overdueDays,
      principalDue,
      lateChargeDue: scenario.lateChargeDue,
      totalDue,
    },
    contact: {
      lastContactedAt: contactLogs[0]?.contactedAt ?? null,
      recentNonLine: contactLogs,
      recentLine: [],
      lineCount: 0,
      historyTotalCount: contactLogs.length,
      historyNextPage: null,
    },
    // 約束の中身は dunningPromises で渡す（ここに期日を入れると旧データの参考表示が重なる）
    promise: { active: !!scenario.activePromise },
    hold: { active: false },
    chat: { lastCustomerMessageAt },
    awaitingReply:
      scenario.routingMode === "reactive" && lastCustomerMessageAt
        ? { active: true, lastCustomerMessageAt }
        : null,
    lineBlocked,
    bankAccountRegistered: !!scenario.bankAccountRegistered,
    user: {
      userId: 0,
      fullName: EVALUATION_USER_NAME,
      fullFuriganaName: "—",
      birthday: null,
      telephoneNumber: null,
      telephoneNumber2: null,
      lineBlocked,
      userAuthId: null,
    },
    // 架空の振込先（AI 入力に振込先の行として渡る）。督促画面と同じ欄に出す
    virtualAccount: scenario.virtualAccount
      ? { id: 0, branchCode: null, ...scenario.virtualAccount }
      : null,
    simplifiedExaminations: {
      latest: {
        id: 0,
        companyName: scenario.companyName || null,
        companyNameKana: null,
        companyTelephoneNumber: null,
        payday: scenario.payday,
        createdAt: scenario.asOf,
      },
      history: [],
    },
    contractDetails: details,
    dunningPromises: scenarioPromises(scenario),
  }
}

/** 実際の督促ケース詳細のヘッダー・パネルに渡すケース（読み取り専用。ID は架空） */
export function scenarioCaseItem(scenario: Scenario): DunningCaseItem {
  return {
    id: 0,
    dunningCase: {
      userId: 0,
      userName: EVALUATION_USER_NAME,
      userFuriganaName: null,
      status: "open",
      openedAt: scenario.asOf,
      closedAt: null,
      openReasonCode: null,
      closeReasonCode: null,
      closeNote: null,
      pausedAt: null,
      pauseReason: null,
      resolvedAt: null,
      openedBySmootherId: null,
      strategyNotes: null,
      noCallToUser: !!scenario.noCallToUser,
      noCallToWorkplace: !!scenario.noCallToWorkplace,
      noCallToEmergencyContact: !!scenario.noCallToEmergencyContact,
      contractLinks: [],
    },
    facts: scenarioFacts(scenario),
    projection: null,
    auditLog: null,
  }
}

/** 会話を督促チャットの吹き出しの形にする（日時順）。当社の発言は送信者名を「オペレーター」にする */
export function scenarioChatMessages(scenario: Scenario): DunningChatMessage[] {
  return [...scenario.conversation]
    .map((message, index) => ({ message, index }))
    .sort((a, b) => Date.parse(a.message.at) - Date.parse(b.message.at))
    .map(({ message, index }) => ({
      messageId: index + 1,
      direction: message.direction,
      contentType: "text",
      // 画像は「画像を受信した」ことだけが AI に渡る
      body:
        message.contentType === "image"
          ? `［受信画像］${message.body ? `\n${message.body}` : ""}`
          : message.body,
      platform: "line",
      sentAt: message.at,
      source: "line",
      isDunning: false,
      senderName: message.direction === "outbound" ? "オペレーター" : null,
    }))
}
